import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControlLabel,
  Grid,
  Link,
  Stack,
  Switch,
  Tab,
  TextField,
  Typography,
} from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Div from "@jumbo/shared/Div/Div";

/**
 * Notifikácie objednávok - povolené kanály smerom na guesta (objednávateľa).
 *
 * Zámerne NEobsahuje zoznam príjemcov: notifikácia o objednávke ide vždy
 * objednávateľovi, nikdy zákazníkovi (prevádzkovateľovi kioskov). Sú to teda
 * len povolenia kanálov, nie adresy.
 *
 * Email a SMS sú pod "Registrovaní zákazníci", lebo anonymnému hosťovi nemáme
 * kam napísať - jeho jediný kanál je push na sledovaciu stránku objednávky.
 *
 * Ukladá sa do dsokSettings.settings.orderNotifications, teda mimo cesty sekcie
 * "settings" (["customer"]). Preto ho DsokCustomerSettingsPage posiela v payloade
 * ako _orderNotifications a layout ho rozbalí - rovnaký vzor ako _dsokPos.
 */

const BASE = "dsokCustomerDetail.sections.orderNotifications";

export const ORDER_NOTIFICATIONS_SCHEMA = {
  // Hlavný spínač dostupnosti služby pre zákazníka. Ak je vypnutý, neodchádza
  // guestovi žiadna notifikácia bez ohľadu na ostatné prepínače.
  // Predvolene vypnutý - službu nemá mať každý zákazník automaticky.
  isActive: false,
  // Odosielateľ SMS (Brevo alfanumerický sender, max 11 znakov). Každý zákazník
  // má vlastného. Ak je prázdny, notifikačný server použije globálny default.
  smsSender: "",
  // Zobraziť kód vyzdvihnutia? Ak áno, ukáže sa na sledovacej stránke aj v SMS;
  // ak nie, neposiela sa ani nezobrazuje.
  showPickupCode: false,
  // Voliteľný individuálny HTML text zobrazený na sledovacej stránke nad
  // históriou stavov (WYSIWYG).
  trackingPageText: "",
  pushOrderPage: { isActive: false },
  pushApp: { isActive: false },
  registeredGuests: {
    email: { isActive: false },
    sms: { isActive: false },
  },
  // Operátorská výdajová obrazovka (order status screen). Ak je zapnutá, v
  // Nastavení sa zobrazí jej URL na otvorenie v novom okne.
  orderStatusScreen: { isActive: false },
  // Výdajový display – read-only zobrazovacia obrazovka stavov objednávok
  // (/order-status-screen/...). Ak je zapnutá, v Nastavení sa zobrazí jej URL.
  dispensingDisplay: { isActive: false },
  // Automatické vyzdvihnutie: objednávka "ready", ktorú zákazník do `minutes`
  // minút nepotvrdí, sa backendom automaticky prepne na "completed".
  autoComplete: { isActive: false, minutes: 10 },
};

const AUTO_COMPLETE_MIN = 1;
const AUTO_COMPLETE_MAX = 180;
const AUTO_COMPLETE_DEFAULT = 10;
const clampAutoCompleteMinutes = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return AUTO_COMPLETE_DEFAULT;
  return Math.min(AUTO_COMPLETE_MAX, Math.max(AUTO_COMPLETE_MIN, Math.round(n)));
};

/** Doplní chýbajúce kľúče, aby prepínače nikdy nedostali undefined. */
export const buildOrderNotificationsInitialValues = (stored) => ({
  ...ORDER_NOTIFICATIONS_SCHEMA,
  ...(stored ?? {}),
  isActive: stored?.isActive === true,
  smsSender: typeof stored?.smsSender === "string" ? stored.smsSender : "",
  showPickupCode: stored?.showPickupCode === true,
  trackingPageText:
    typeof stored?.trackingPageText === "string" ? stored.trackingPageText : "",
  pushOrderPage: {
    ...ORDER_NOTIFICATIONS_SCHEMA.pushOrderPage,
    ...(stored?.pushOrderPage ?? {}),
  },
  pushApp: {
    ...ORDER_NOTIFICATIONS_SCHEMA.pushApp,
    ...(stored?.pushApp ?? {}),
  },
  registeredGuests: {
    email: {
      ...ORDER_NOTIFICATIONS_SCHEMA.registeredGuests.email,
      ...(stored?.registeredGuests?.email ?? {}),
    },
    sms: {
      ...ORDER_NOTIFICATIONS_SCHEMA.registeredGuests.sms,
      ...(stored?.registeredGuests?.sms ?? {}),
    },
  },
  orderStatusScreen: {
    ...ORDER_NOTIFICATIONS_SCHEMA.orderStatusScreen,
    ...(stored?.orderStatusScreen ?? {}),
  },
  dispensingDisplay: {
    ...ORDER_NOTIFICATIONS_SCHEMA.dispensingDisplay,
    ...(stored?.dispensingDisplay ?? {}),
  },
  autoComplete: {
    isActive: stored?.autoComplete?.isActive === true,
    minutes: clampAutoCompleteMinutes(stored?.autoComplete?.minutes),
  },
});

const ToggleRow = ({ name, label, helpText, checked, onToggle, readOnly }) => (
  <Grid item xs={12} md={6}>
    <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
      <FormControlLabel
        control={
          <Switch
            checked={checked === true}
            onChange={(e) => onToggle(name, e.target.checked)}
            disabled={readOnly}
          />
        }
        label={label}
      />
      {helpText ? (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ whiteSpace: "pre-line" }}
        >
          {helpText}
        </Typography>
      ) : null}
    </Div>
  </Grid>
);

const OrderNotificationsTab = ({ readOnly = false }) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext();

  const cfg = values?.orderNotifications ?? ORDER_NOTIFICATIONS_SCHEMA;
  const onToggle = (name, checked) => setFieldValue(name, checked);

  // Hlavný spínač: kým je vypnutý, ostatné voľby nemajú význam, preto sú
  // zablokované. Nezhasínajú sa - keď sa služba znova zapne, zákazník nájde
  // svoje pôvodné nastavenie.
  const serviceEnabled = cfg?.isActive === true;
  const childDisabled = readOnly || !serviceEnabled;

  // SMS má vlastný spínač: kým je vypnutý, na sledovacej stránke sa nezobrazí
  // pole na telefónne číslo a SMS sa neposielajú (odosielateľ a kód vyzdvihnutia
  // nemajú význam, preto sú zablokované).
  const smsEnabled = cfg?.registeredGuests?.sms?.isActive === true;
  const smsDisabled = childDisabled || !smsEnabled;

  // Vnorené taby v rámci "Management objednávok": Nastavenie a Notifikácie
  // (súčasný obsah). Default = Notifikácie, aby obrazovka nebola prázdna.
  const [innerTab, setInnerTab] = useState("notifications");

  // Order status screen (operátorská výdajová obrazovka). URL sa skladá rovnako
  // ako v header.shared.js: code/locationCode/env/customerId, env podľa beDomain.
  const statusScreenEnabled = cfg?.orderStatusScreen?.isActive === true;
  const dispensingDisplayEnabled = cfg?.dispensingDisplay?.isActive === true;

  // Spoločné parametre pre operátorské URL. env sa AUTOMATICKY generuje podľa
  // zákazníka (beDomain: staging → stage, localhost → dev, inak prod).
  const screenBase = (() => {
    const c = values?.customer || {};
    const beDomain = c?.beDomain || "";
    let env = "prod";
    if (beDomain.includes("staging")) env = "stage";
    if (
      typeof window !== "undefined" &&
      window.location?.origin?.includes("localhost")
    ) {
      env = "dev";
    }
    const locationCode =
      c?.locations?.[1]?.code ||
      Object.values(c?.locations || {})[0]?.code ||
      null;
    const customerId = c?.customerId ?? c?.id ?? null;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return { env, code: c?.code || null, locationCode, customerId, origin };
  })();

  // Order status screen (operátorská výdajová/dispensing obrazovka).
  const orderStatusScreenUrl =
    screenBase.code && screenBase.locationCode && screenBase.customerId != null
      ? `${screenBase.origin}/customer/dsok/dispensing-orders/${screenBase.code}/${screenBase.locationCode}/${screenBase.env}/${screenBase.customerId}`
      : null;

  // Výdajový display (read-only zobrazovacia obrazovka stavov objednávok).
  const dispensingDisplayUrl =
    screenBase.code && screenBase.locationCode
      ? `${screenBase.origin}/order-status-screen/${screenBase.code}/${screenBase.locationCode}/${screenBase.env}/sk-SK/15000/restaurant`
      : null;

  return (
    <TabContext value={innerTab}>
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <TabList onChange={(_, v) => setInnerTab(v)}>
          <Tab
            label={t(`${BASE}.innerTabs.settings`, "Nastavenie")}
            value="settings"
          />
          <Tab
            label={t(`${BASE}.innerTabs.notifications`, "Notifikácie")}
            value="notifications"
          />
        </TabList>
      </Box>
      <TabPanel value="settings" sx={{ p: 0, minHeight: 120 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {t(`${BASE}.orderStatusScreen.section`, "Order status screen")}
            </Typography>
            <Divider sx={{ mb: 1 }} />
          </Grid>
          <ToggleRow
            name="orderNotifications.orderStatusScreen.isActive"
            label={t(
              `${BASE}.orderStatusScreen.enabled`,
              "Povoliť order status screen",
            )}
            helpText={t(
              `${BASE}.orderStatusScreen.enabledHelp`,
              "Operátorská výdajová obrazovka objednávok. Ak je zapnutá, nižšie sa zobrazí jej URL na otvorenie v novom okne.",
            )}
            checked={statusScreenEnabled}
            onToggle={onToggle}
            readOnly={readOnly}
          />
          {statusScreenEnabled ? (
            <Grid item xs={12}>
              <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
                {orderStatusScreenUrl ? (
                  <Stack
                    direction="row"
                    gap={1.5}
                    alignItems="center"
                    flexWrap="wrap"
                  >
                    <Link
                      href={orderStatusScreenUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        wordBreak: "break-all",
                        fontFamily: "monospace",
                        fontSize: 13,
                      }}
                    >
                      {orderStatusScreenUrl}
                    </Link>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<OpenInNewIcon />}
                      onClick={() =>
                        window.open(
                          orderStatusScreenUrl,
                          "_blank",
                          "noopener,noreferrer",
                        )
                      }
                    >
                      {t(
                        `${BASE}.orderStatusScreen.open`,
                        "Otvoriť v novom okne",
                      )}
                    </Button>
                  </Stack>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    {t(
                      `${BASE}.orderStatusScreen.missingData`,
                      "URL sa nedá zostaviť — chýba kód zákazníka, lokalita alebo ID zákazníka.",
                    )}
                  </Typography>
                )}
              </Div>
            </Grid>
          ) : null}

          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              {t(`${BASE}.dispensingDisplay.section`, "Výdajový display")}
            </Typography>
            <Divider sx={{ mb: 1 }} />
          </Grid>
          <ToggleRow
            name="orderNotifications.dispensingDisplay.isActive"
            label={t(
              `${BASE}.dispensingDisplay.enabled`,
              "Povoliť výdajový display",
            )}
            helpText={t(
              `${BASE}.dispensingDisplay.enabledHelp`,
              "Read-only zobrazovacia obrazovka stavov objednávok. Ak je zapnutá, nižšie sa zobrazí jej URL na otvorenie v novom okne.",
            )}
            checked={dispensingDisplayEnabled}
            onToggle={onToggle}
            readOnly={readOnly}
          />
          {dispensingDisplayEnabled ? (
            <Grid item xs={12}>
              <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
                {dispensingDisplayUrl ? (
                  <Stack
                    direction="row"
                    gap={1.5}
                    alignItems="center"
                    flexWrap="wrap"
                  >
                    <Link
                      href={dispensingDisplayUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        wordBreak: "break-all",
                        fontFamily: "monospace",
                        fontSize: 13,
                      }}
                    >
                      {dispensingDisplayUrl}
                    </Link>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<OpenInNewIcon />}
                      onClick={() =>
                        window.open(
                          dispensingDisplayUrl,
                          "_blank",
                          "noopener,noreferrer",
                        )
                      }
                    >
                      {t(
                        `${BASE}.orderStatusScreen.open`,
                        "Otvoriť v novom okne",
                      )}
                    </Button>
                  </Stack>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    {t(
                      `${BASE}.orderStatusScreen.missingData`,
                      "URL sa nedá zostaviť — chýba kód zákazníka, lokalita alebo ID zákazníka.",
                    )}
                  </Typography>
                )}
              </Div>
            </Grid>
          ) : null}

          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              {t(
                `${BASE}.autoComplete.section`,
                "Automatické vyzdvihnutie",
              )}
            </Typography>
            <Divider sx={{ mb: 1 }} />
          </Grid>
          <ToggleRow
            name="orderNotifications.autoComplete.isActive"
            label={t(
              `${BASE}.autoComplete.enabled`,
              "Automaticky označiť za vyzdvihnuté",
            )}
            helpText={t(
              `${BASE}.autoComplete.enabledHelp`,
              "Objednávku pripravenú na vyzdvihnutie, ktorú zákazník do zadaného času nepotvrdí, systém automaticky označí za vyzdvihnutú.",
            )}
            checked={cfg?.autoComplete?.isActive === true}
            onToggle={onToggle}
            readOnly={readOnly}
          />
          {cfg?.autoComplete?.isActive === true ? (
            <Grid item xs={12} md={6}>
              <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label={t(
                    `${BASE}.autoComplete.minutes`,
                    "Čas do automatického vyzdvihnutia (min)",
                  )}
                  value={cfg?.autoComplete?.minutes ?? AUTO_COMPLETE_DEFAULT}
                  onChange={(e) =>
                    setFieldValue(
                      "orderNotifications.autoComplete.minutes",
                      e.target.value === "" ? "" : Number(e.target.value),
                    )
                  }
                  onBlur={(e) =>
                    setFieldValue(
                      "orderNotifications.autoComplete.minutes",
                      clampAutoCompleteMinutes(e.target.value),
                    )
                  }
                  inputProps={{
                    min: AUTO_COMPLETE_MIN,
                    max: AUTO_COMPLETE_MAX,
                    step: 1,
                  }}
                  disabled={readOnly}
                  helperText={t(
                    `${BASE}.autoComplete.minutesHelp`,
                    "Predvolene 10 minút, rozsah 1–180.",
                  )}
                />
              </Div>
            </Grid>
          ) : null}
        </Grid>
      </TabPanel>
      <TabPanel value="notifications" sx={{ p: 0 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Alert severity="info" sx={{ mb: 2 }}>
          {t(`${BASE}.info`)}
        </Alert>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {t(`${BASE}.service`)}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <ToggleRow
            name="orderNotifications.isActive"
            label={t(`${BASE}.enabled`)}
            helpText={t(`${BASE}.enabledHelp`)}
            checked={serviceEnabled}
            onToggle={onToggle}
            readOnly={readOnly}
          />
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {t(`${BASE}.push`)}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <ToggleRow
            name="orderNotifications.pushOrderPage.isActive"
            label={t(`${BASE}.pushOrderPage`)}
            helpText={t(`${BASE}.pushOrderPageHelp`)}
            checked={cfg?.pushOrderPage?.isActive}
            onToggle={onToggle}
            readOnly={childDisabled}
          />
          <ToggleRow
            name="orderNotifications.pushApp.isActive"
            label={t(`${BASE}.pushApp`)}
            helpText={t(`${BASE}.pushAppHelp`)}
            checked={cfg?.pushApp?.isActive}
            onToggle={onToggle}
            readOnly={childDisabled}
          />
          <Grid item xs={12}>
            <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {t(`${BASE}.trackingPageText`)}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 1 }}
              >
                {t(`${BASE}.trackingPageTextHelp`)}
              </Typography>
              <Box sx={{ pointerEvents: childDisabled ? "none" : "auto", opacity: childDisabled ? 0.5 : 1 }}>
                <ReactQuill
                  value={cfg?.trackingPageText || ""}
                  readOnly={childDisabled}
                  onChange={(content) =>
                    setFieldValue("orderNotifications.trackingPageText", content)
                  }
                  modules={{
                    toolbar: [
                      ["bold", "italic", "underline"],
                      [{ list: "ordered" }, { list: "bullet" }],
                      ["link"],
                      ["clean"],
                    ],
                  }}
                />
              </Box>
            </Div>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {t(`${BASE}.smsSettings`)}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <ToggleRow
            name="orderNotifications.registeredGuests.sms.isActive"
            label={t(`${BASE}.smsEnabled`)}
            helpText={t(`${BASE}.smsEnabledHelp`)}
            checked={smsEnabled}
            onToggle={onToggle}
            readOnly={childDisabled}
          />
          <Grid item xs={12} md={6}>
            <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
              <TextField
                fullWidth
                size="small"
                label={t(`${BASE}.smsSender`)}
                value={cfg?.smsSender ?? ""}
                onChange={(e) =>
                  setFieldValue("orderNotifications.smsSender", e.target.value)
                }
                disabled={smsDisabled}
                inputProps={{ maxLength: 11 }}
                helperText={t(`${BASE}.smsSenderHelp`)}
              />
            </Div>
          </Grid>
          <ToggleRow
            name="orderNotifications.showPickupCode"
            label={t(`${BASE}.showPickupCode`)}
            helpText={t(`${BASE}.showPickupCodeHelp`)}
            checked={cfg?.showPickupCode}
            onToggle={onToggle}
            readOnly={smsDisabled}
          />
        </Grid>
      </Grid>
        </Grid>
      </TabPanel>
    </TabContext>
  );
};

export default OrderNotificationsTab;
